import React from "react";
import { PlantingIcon } from "./icons/planting";
import {
    IPlantingEventFormProps,
    PlantingEventForm as PlantingEventFormView,
} from "./event-planting-form";
import { EventPlantingInfo } from "./event-planting-info";
import { messages } from "./i18n-messages";

import { connect } from "react-redux";
import { fetchDropdownData } from "~/core/dropdowns/actions";
import {
    actions as picklistActions,
    selectors as picklistSelectors,
    picklistNames,
} from "~/core/picklist";
import { actions as unitActions, selectors as unitSelectors, unitNames } from "~/core/units";
import * as selectors from "../../event-module/components/event-info/selectors";
import * as actions from "../../event-module/components/event-info/actions";
import { getUserPreference } from "~/admin/setup/preference/data/selectors";
import { currentUserHasRole } from "~/login/selectors";
import { PROPS_ROLE_ENABLE_MYAGDATA } from "~/admin/setup/user-role/data/model";
import { getMyAgDataCommodities } from "../../event-module/components/event-info/selectors";
import { requireMyAgDataCommodity } from "~/recs-events/events/selectors";
import { toCropDropdownOptions, useCropList } from "~/utils/crops/use-crop-list";
import { useLoggedInUserProductScopeOrgLevelGuid } from "~/utils/products/use-product-list";

const mapStateToProps = (state): Partial<IPlantingEventFormProps> => {
    const rowOrientation = picklistSelectors.getPicklistOptionsFromCode(
        state,
        picklistNames.getPickListCode(picklistNames.PICKLIST_ROW_ORIENTATION)
    );

    return {
        brandOrgData: selectors.getBrandOrgState(state),
        cropPurpose: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROP_PURPOSE)
        ),
        dryPriceUnit: unitSelectors.getUnitPicklistFromCode(
            state,
            unitNames.getUnitCode(unitNames.UNIT_PRICE_UNIT_DRY)
        ),
        dryProductUnit: unitSelectors.getUnitPicklistFromCode(
            state,
            unitNames.getUnitCode(unitNames.UNIT_PRODUCT_RATE_DRY)
        ),
        liquidPriceUnit: unitSelectors.getUnitPicklistFromCode(
            state,
            unitNames.getUnitCode(unitNames.UNIT_PRICE_UNIT_LIQUID)
        ),
        liquidProducteUnit: unitSelectors.getUnitPicklistFromCode(
            state,
            unitNames.getUnitCode(unitNames.UNIT_PRODUCT_RATE_LIQUID)
        ),
        packageUnit: unitSelectors.getUnitPicklistFromCode(
            state,
            unitNames.getUnitCode(unitNames.UNIT_PACKAGE)
        ),
        plantSpacing: picklistSelectors.getPicklistFromCode(state, "plantSpacing"),
        plantingCropAge: picklistSelectors.getPicklistFromCode(state, "plantingCropAge"),
        plantingDepth: picklistSelectors.getPicklistFromCode(state, "plantingDepth"),
        plantingPattern: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_PLANTING_PATTERN)
        ),
        productData: selectors.getProductData(state),
        rowOrientation: rowOrientation.sort((a, b) =>
            a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        ),
        rowSpacing: picklistSelectors.getPicklistFromCode(state, "rowSpacing"),
        seedingRate: picklistSelectors.getPicklistFromCode(state, "seedingRate"),
        transplantRate: picklistSelectors.getPicklistFromCode(state, "transplantRate"),
        transplantType: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_TRANSPLANT_TYPE)
        ),
        varietyHybridData: selectors.getVarietyHybridDropdownState(state),
        [picklistNames.PICKLIST_COMMODITY]: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_COMMODITY)
        ),
        [picklistNames.PICKLIST_COMMODITY_TYPE]: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_COMMODITY_TYPE)
        ),
        [picklistNames.PICKLIST_INTENDED_USE]: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_INTENDED_USE)
        ),
        [picklistNames.PICKLIST_CROPPING_PRACTICE]: picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_PRACTICE)
        ),
        enableMyAgData:
            (getUserPreference(state)?.enableMyAgData ?? false) &&
            currentUserHasRole(state, PROPS_ROLE_ENABLE_MYAGDATA),
        myAgDataCommodities: getMyAgDataCommodities(state),
        requireCommodity: requireMyAgDataCommodity(state),
    };
};

const mapDispatchToProps = (dispatch): Partial<IPlantingEventFormProps> => ({
    fetchDropdownData: (dropdowns) =>
        dispatch(
            fetchDropdownData({
                ...dropdowns,
                action: actions.fetchedDropdownData,
            })
        ),
    fetchPicklistData: (payload) => dispatch(picklistActions.fetchPicklistData(payload)),
    fetchUnitData: (payload) => dispatch(unitActions.fetchUnitData(payload)),
    fetchSystemAttributePicklist: (payload) =>
        dispatch(actions.fetchSystemAttributePicklist(payload)),
    fetchMyAgDataCommodities: () => dispatch(actions.fetchMyAgDataCommodities()),
});

export const formLabelIcon = PlantingIcon;
export const formLabelMessage = messages.plantingFormLabelText;
export const SummarySection = EventPlantingInfo;

const ConnectedPlantingEventForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(PlantingEventFormView);

export const PlantingEventForm = (props: Partial<IPlantingEventFormProps>) => {
    const orgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { crops } = useCropList({ orgLevelGuid });

    return <ConnectedPlantingEventForm {...props} cropData={toCropDropdownOptions(crops)} />;
};
